import { useState, useEffect } from "react";
import {
  ArrowUpRight,
  ArrowDownLeft,
  Copy,
  ExternalLink,
  RefreshCw,
  PieChart,
  ArrowDownCircle,
  ArrowUpCircle,
  CheckCircle,
  AlertCircle,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { useSelector } from "react-redux";
import { RootState } from "../store";
import { getRecentTransactions, getTransactionsVerified, VerifiedTransaction, VerificationSummary } from "../services/authapi";
import toast, { Toaster } from "react-hot-toast";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../firebase";
import {
  parse,
  isValid,
  startOfDay,
  endOfDay,
  subDays,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  format,
} from "date-fns";

interface User {
  uid: string;
  email: string;
  wallet: string;
  username: string;
  joinedAt: string;
}

interface Transaction {
  id: string;
  type: "sent" | "received";
  amount: number;
  from: string;
  to: string;
  date: string;
  hash: string;
}

interface ExpandedTransactions {
  [key: string]: boolean;
}


const AnalyticsPage = () => {
  const [rawTransactions, setRawTransactions] = useState<Transaction[]>([]);
  const [displayTransactions, setDisplayTransactions] = useState<Transaction[]>(
    [],
  );
  const [totalReceived, setTotalReceived] = useState<number>(0);
  const [totalSent, setTotalSent] = useState<number>(0);
  const [addressToUsername, setAddressToUsername] = useState<{
    [address: string]: string | undefined;
  }>({});
  const [loadingTransactions, setLoadingTransactions] =
    useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState<
    "today" | "yesterday" | "weekly" | "monthly" | "yearly"
  >("monthly");
  const [periodName, setPeriodName] = useState<string>("");

  // Verification-related state
  const [verifiedTransactions, setVerifiedTransactions] = useState<VerifiedTransaction[]>([]);
  const [verificationSummary, setVerificationSummary] = useState<VerificationSummary | null>(null);
  const [loadingVerification, setLoadingVerification] = useState<boolean>(false);
  const [useVerification, setUseVerification] = useState<boolean>(false);
  const [expandedDetails, setExpandedDetails] = useState<ExpandedTransactions>({});
  const [customFromDate, setCustomFromDate] = useState<Date>(new Date(new Date().setDate(1)));
  const [customToDate, setCustomToDate] = useState<Date>(new Date());

  const periodOptions: {
    key: "today" | "yesterday" | "weekly" | "monthly" | "yearly";
    label: string;
  }[] = [
    { key: "today", label: "Today" },
    { key: "yesterday", label: "Yesterday" },
    { key: "weekly", label: "Week" },
    { key: "monthly", label: "Month" },
    { key: "yearly", label: "Year" },
  ];

  const periodTitle: Record<typeof period, string> = {
    today: "Today's Analytics",
    yesterday: "Yesterday's Analytics",
    weekly: "Weekly Analytics",
    monthly: "Monthly Analytics",
    yearly: "Yearly Analytics",
  };

  const formatAmount = (amount: number): string => {
    if (!isFinite(amount)) return "0";
    return parseFloat(amount.toFixed(2)).toString();
  };

  // Returns [start, end] for the currently-selected period
  const getPeriodRange = (p: typeof period): [Date, Date] => {
    const now = new Date();
    switch (p) {
      case "today":
        return [startOfDay(now), endOfDay(now)];
      case "yesterday": {
        const y = subDays(now, 1);
        return [startOfDay(y), endOfDay(y)];
      }
      case "weekly":
        return [startOfWeek(now, { weekStartsOn: 1 }), endOfWeek(now, { weekStartsOn: 1 })];
      case "monthly":
        return [startOfMonth(now), endOfMonth(now)];
      case "yearly":
        return [startOfYear(now), endOfYear(now)];
    }
  };

  const user = useSelector((state: RootState) => state.user) as User | null;
  const walletAddress = (user?.wallet || "").toLowerCase();
  const walletAddressWithoutLowerCase = user?.wallet || "";
  const adminWallet = import.meta.env.VITE_Admin_Wallet?.toLowerCase() || "";

  // Update period name when period changes
  useEffect(() => {
    const [start, end] = getPeriodRange(period);
    switch (period) {
      case "today":
        setPeriodName(format(start, "MMMM d, yyyy"));
        break;
      case "yesterday":
        setPeriodName(format(start, "MMMM d, yyyy"));
        break;
      case "weekly":
        setPeriodName(`${format(start, "MMM d")} – ${format(end, "MMM d, yyyy")}`);
        break;
      case "monthly":
        setPeriodName(format(start, "MMMM yyyy"));
        break;
      case "yearly":
        setPeriodName(format(start, "yyyy"));
        break;
    }
  }, [period]);

  // Fetch verified transactions for custom date range
  const fetchVerifiedTransactions = async () => {
    if (!walletAddress) {
      setError("No wallet address found for the user.");
      toast.error("No wallet address found for the user.");
      return;
    }

    // Validate date range
    if (customFromDate > customToDate) {
      toast.error("From date must be before To date");
      return;
    }

    setLoadingVerification(true);
    try {
      const txs = await getTransactionsVerified(
        walletAddressWithoutLowerCase,
        customFromDate,
        customToDate
      );
      console.log("Fetched verified transactions:", txs);
      setVerifiedTransactions(txs);

      // Calculate totals for custom date range
      const received = txs.reduce(
        (sum, tx) => (tx.type === "received" ? sum + tx.amount : sum),
        0,
      );
      const sent = txs.reduce(
        (sum, tx) => (tx.type === "sent" ? sum + tx.amount : sum),
        0,
      );

      setTotalReceived(received);
      setTotalSent(sent);

      // Calculate verification summary
      const summary = {
        total: txs.length,
        verified: txs.filter((tx) => tx.verificationStatus === "verified").length,
        dbOnly: txs.filter((tx) => tx.verificationStatus === "db_only").length,
        chainOnly: txs.filter((tx) => tx.verificationStatus === "chain_only").length,
        verificationRate:
          txs.length > 0
            ? (
                (txs.filter((tx) => tx.verificationStatus === "verified").length /
                  txs.length) *
                100
              ).toFixed(1) + "%"
            : "0%",
      };
      setVerificationSummary(summary);
      setError(null);
    } catch (err: any) {
      console.error("Error fetching verified transactions:", err.message);
      toast.error("Failed to fetch verified transactions.");
      setError("Failed to fetch verified transactions: " + err.message);
    } finally {
      setLoadingVerification(false);
    }
  };

  // Fetch transactions
  useEffect(() => {
    const fetchTransactions = async () => {
      if (!walletAddress) {
        setError("No wallet address found for the user.");
        toast.error("No wallet address found for the user.");
        return;
      }

      setLoadingTransactions(true);
      try {
        const txs = await getRecentTransactions(walletAddressWithoutLowerCase);
        console.log("Fetched transactions:", txs);
        setRawTransactions(txs);
        setError(null);
      } catch (err: any) {
        console.error("Error fetching transactions:", err.message);
        toast.error("Failed to fetch transactions. Please try again.");
        setError("Failed to fetch transactions: " + err.message);
      } finally {
        setLoadingTransactions(false);
      }
    };

    fetchTransactions();
  }, [walletAddress]);

  // Fetch usernames for better display
  useEffect(() => {
    const fetchProfiles = async () => {
      try {
        const usersSnap = await getDocs(collection(db, "users"));
        const vendorsSnap = await getDocs(collection(db, "vendors"));
        const map: { [address: string]: string | undefined } = {};

        usersSnap.forEach((doc) => {
          const data = doc.data();
          if (data.wallet) {
            map[data.wallet.toLowerCase()] = data.username;
          }
        });

        vendorsSnap.forEach((doc) => {
          const data = doc.data();
          if (data.wallet) {
            map[data.wallet.toLowerCase()] = data.name;
          }
        });

        setAddressToUsername(map);
      } catch (err: any) {
        console.error("Error fetching profiles:", err.message);
        toast.error("Failed to fetch profiles. Please try again.");
      }
    };

    fetchProfiles();
  }, []);

  // Filter transactions & calculate totals whenever raw data or period changes
  useEffect(() => {
    const [start, end] = getPeriodRange(period);

    // Flexible date parsing
    const parseTxDate = (dateStr: string): Date | null => {
      const formats = [
        "M/d/yyyy, h:mm:ss a",
        "MM/dd/yyyy, h:mm:ss a",
        "M/d/yyyy h:mm:ss a",
        "MM/dd/yyyy h:mm:ss a",
        "yyyy-MM-dd HH:mm:ss",
      ];

      for (const fmt of formats) {
        const parsed = parse(dateStr, fmt, new Date());
        if (isValid(parsed)) {
          return parsed;
        }
      }
      console.warn(`Failed to parse date: "${dateStr}"`);
      return null;
    };

    // Filter for current period
    const periodFiltered = rawTransactions.filter((tx) => {
      const txDate = parseTxDate(tx.date);
      return txDate && txDate >= start && txDate <= end;
    });

    // Calculate totals (includes fees)
    const received = periodFiltered.reduce(
      (sum, tx) => (tx.type === "received" ? sum + tx.amount : sum),
      0,
    );
    const sent = periodFiltered.reduce(
      (sum, tx) => (tx.type === "sent" ? sum + tx.amount : sum),
      0,
    );

    setTotalReceived(received);
    setTotalSent(sent);

    // Filter out admin fee transactions for display
    const displayTxs = periodFiltered.filter((tx) => {
      if (tx.type === "sent") {
        return tx.to.toLowerCase() !== adminWallet;
      }
      return true;
    });

    setDisplayTransactions(displayTxs);
  }, [rawTransactions, period, adminWallet]);



  const totalFlow = totalReceived + totalSent;
  const receivedDeg = totalFlow > 0 ? (totalReceived / totalFlow) * 360 : 0;

  const pieStyle =
    totalFlow > 0
      ? {
          background: `conic-gradient(#61CE70 0deg ${receivedDeg}deg, #EF4444 ${receivedDeg}deg 360deg)`,
        }
      : { background: "#374151" };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  const handleRefresh = async () => {
    if (!walletAddress) {
      setError("No wallet address found for the user.");
      toast.error("No wallet address found for the user.");
      return;
    }

    setLoadingTransactions(true);
    try {
      const txs = await getRecentTransactions(walletAddressWithoutLowerCase);
      setRawTransactions(txs);
      setError(null);
      toast.success("Transactions refreshed!");
    } catch (err: any) {
      console.error("Error refreshing transactions:", err.message);
      toast.error("Failed to refresh transactions.");
      setError("Failed to refresh transactions: " + err.message);
    } finally {
      setLoadingTransactions(false);
    }
  };

  const truncateAddress = (address: string): string => {
    if (!address) return "";
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const getCounterpartyDisplay = (tx: Transaction): string => {
    const counterparty =
      tx.type === "received" ? tx.from.toLowerCase() : tx.to.toLowerCase();
    return addressToUsername[counterparty] || truncateAddress(counterparty);
  };

  const viewOnBlockExplorer = (hash: string) => {
    window.open(`https://basescan.org/tx/${hash}`, "_blank");
  };

  const toggleExpandDetails = (txId: string) => {
    setExpandedDetails((prev) => ({
      ...prev,
      [txId]: !prev[txId],
    }));
  };

  const getVerificationBadgeColor = (status: string) => {
    switch (status) {
      case "verified":
        return "bg-green-500/20 text-green-400 border-green-500/30";
      case "db_only":
        return "bg-yellow-500/20 text-yellow-400 border-yellow-500/30";
      case "chain_only":
        return "bg-red-500/20 text-red-400 border-red-500/30";
      default:
        return "bg-gray-600/20 text-gray-400 border-gray-600/30";
    }
  };

  const getVerificationIcon = (status: string) => {
    switch (status) {
      case "verified":
        return <CheckCircle className="w-4 h-4" />;
      case "db_only":
        return <AlertTriangle className="w-4 h-4" />;
      case "chain_only":
        return <AlertCircle className="w-4 h-4" />;
      default:
        return <AlertCircle className="w-4 h-4" />;
    }
  };

  const getVerificationLabel = (status: string) => {
    switch (status) {
      case "verified":
        return "On-Chain Verified";
      case "db_only":
        return "DB Only";
      case "chain_only":
        return "Chain Only";
      default:
        return "Unverified";
    }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-8 p-4 sm:p-0">
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: "#0F1419",
            color: "#fff",
            border: "1px solid #374151",
            borderRadius: "12px",
            fontSize: "14px",
          },
          success: {
            iconTheme: {
              primary: "#61CE70",
              secondary: "#fff",
            },
          },
          error: {
            iconTheme: {
              primary: "#EF4444",
              secondary: "#fff",
            },
          },
        }}
      />

      <div className="mb-8">
        <h1 className="text-3xl md:text-4xl font-bold text-white mb-3 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
          {periodTitle[period]}
        </h1>
        <p className="text-gray-400 text-lg">
          Overview of your wallet activity for {periodName}
        </p>
      </div>

      {/* Tab Switcher */}
      <div className="flex flex-wrap gap-2 mb-6">
        {periodOptions.map(({ key, label }) => (
          <button
            key={key}
            onClick={() => {
              setPeriod(key);
              setUseVerification(false);
            }}
            className={`px-4 py-2 rounded-xl font-medium text-sm sm:text-base transition-all duration-300 ${
              period === key && !useVerification
                ? "bg-[#61CE70] text-white shadow-md shadow-[#61CE70]/30"
                : "bg-gray-800/50 text-gray-400 hover:bg-gray-700/50"
            }`}
          >
            {label}
          </button>
        ))}
        <button
          onClick={() => setUseVerification(!useVerification)}
          className={`px-4 py-2 rounded-xl font-medium text-sm sm:text-base transition-all duration-300 ${
            useVerification
              ? "bg-blue-600 text-white shadow-md shadow-blue-600/30"
              : "bg-gray-800/50 text-gray-400 hover:bg-gray-700/50"
          }`}
        >
          Custom
        </button>
      </div>

      {/* Custom Date Range Picker - Elegant Professional Design */}
      {useVerification && (
        <div className="bg-gradient-to-b from-gray-800/40 to-gray-900/20 border border-gray-700/30 rounded-lg p-6 mb-6 backdrop-blur-sm">
          {/* Header */}
          <div className="mb-6">
            <h3 className="text-white font-medium text-base mb-1">Date Range</h3>
            <p className="text-gray-400 text-sm">Select the period to analyze</p>
          </div>

          {/* Inputs Container */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
            {/* From Date */}
            <div>
              <label className="text-gray-400 text-xs font-medium mb-2 block">Start Date</label>
              <div className="relative">
                <input
                  type="date"
                  value={customFromDate.toISOString().split("T")[0]}
                  onChange={(e) => {
                    const [year, month, day] = e.target.value.split("-");
                    setCustomFromDate(new Date(`${year}-${month}-${day}T00:00:00Z`));
                  }}
                  className="w-full px-4 py-2.5 bg-gray-800/50 text-white text-sm rounded border border-gray-700/50 hover:border-gray-600/80 focus:border-emerald-500/50 focus:outline-none focus:ring-1 focus:ring-emerald-500/20 transition-all duration-200 appearance-none"
                />
              </div>
            </div>

            {/* To Date */}
            <div>
              <label className="text-gray-400 text-xs font-medium mb-2 block">End Date</label>
              <div className="relative">
                <input
                  type="date"
                  value={customToDate.toISOString().split("T")[0]}
                  onChange={(e) => {
                    const [year, month, day] = e.target.value.split("-");
                    setCustomToDate(new Date(`${year}-${month}-${day}T23:59:59Z`));
                  }}
                  max={new Date().toISOString().split("T")[0]}
                  className="w-full px-4 py-2.5 bg-gray-800/50 text-white text-sm rounded border border-gray-700/50 hover:border-gray-600/80 focus:border-emerald-500/50 focus:outline-none focus:ring-1 focus:ring-emerald-500/20 transition-all duration-200 appearance-none"
                />
              </div>
            </div>
          </div>

          {/* Date Display */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-2">
              <span className="text-gray-500 text-sm">Period:</span>
              <span className="text-white font-medium text-sm">
                {format(customFromDate, "MMM d")} – {format(customToDate, "MMM d, yyyy")}
              </span>
            </div>
          </div>

          {/* Fetch Button */}
          <button
            onClick={fetchVerifiedTransactions}
            disabled={loadingVerification}
            className="w-full sm:w-auto px-6 py-2.5 bg-emerald-600/90 hover:bg-emerald-600 text-white text-sm font-medium rounded transition-all duration-200 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg hover:shadow-emerald-600/20"
          >
            {loadingVerification ? (
              <>
                <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                <span>Fetching...</span>
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A9.001 9.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <span>Fetch Transactions</span>
              </>
            )}
          </button>
        </div>
      )}

      {error && (
        <div className="bg-red-500/10 border border-red-500/50 text-red-400 p-4 rounded-2xl">
          {error}
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
        <div className="bg-gradient-to-br from-[#61CE70]/20 to-[#0F1419] border border-[#61CE70]/30 rounded-3xl p-5 sm:p-6 shadow-lg shadow-[#61CE70]/10 hover:shadow-[#61CE70]/20 transition-all duration-300 transform hover:-translate-y-1">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <ArrowDownCircle className="w-10 h-10 text-[#61CE70] transform hover:scale-110 transition-transform" />
              <h3 className="text-lg sm:text-xl font-semibold text-white">
                Incoming Activity
              </h3>
            </div>
          </div>
          <div className="space-y-4">
            <div>
              <p className="text-sm text-gray-400">Total Received</p>
              <p className="text-2xl sm:text-3xl font-bold text-[#61CE70]">
                {formatAmount(totalReceived)} USD
              </p>
            </div>
          
          </div>
        </div>

        <div className="bg-gradient-to-br from-red-500/20 to-[#0F1419] border border-red-500/30 rounded-3xl p-5 sm:p-6 shadow-lg shadow-red-500/10 hover:shadow-red-500/20 transition-all duration-300 transform hover:-translate-y-1">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <ArrowUpCircle className="w-10 h-10 text-red-400 transform hover:scale-110 transition-transform" />
              <h3 className="text-lg sm:text-xl font-semibold text-white">
                Outgoing Activity
              </h3>
            </div>
          </div>
          <div className="space-y-4">
            <div>
              <p className="text-sm text-gray-400">Total Sent</p>
              <p className="text-2xl sm:text-3xl font-bold text-red-400">
                {formatAmount(totalSent)} USD
              </p>
            </div>
           
          </div>
        </div>
      </div>


      {/* Transaction Flow Pie Chart */}
      {!useVerification && (
        <div className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-3xl p-8 backdrop-blur-sm">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <PieChart className="w-6 h-6 text-white" />
              <h3 className="text-xl font-bold text-white">
                Transaction Flow Distribution
              </h3>
            </div>
          </div>
          <div className="flex flex-col items-center">
            <div
              className="w-48 h-48 sm:w-64 sm:h-64 rounded-full shadow-lg"
              style={pieStyle}
            ></div>
            <div className="flex justify-center space-x-6 mt-6">
              <div className="flex items-center">
                <div className="w-4 h-4 bg-[#61CE70] rounded-full mr-2"></div>
                <span className="text-gray-300">
                  Incoming ({formatAmount(totalReceived)} USD)
                </span>
              </div>
              <div className="flex items-center">
                <div className="w-4 h-4 bg-[#EF4444] rounded-full mr-2"></div>
                <span className="text-gray-300">
                  Outgoing ({formatAmount(totalSent)} USD)
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Transaction History */}
      <div className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-3xl p-8 backdrop-blur-sm">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-white">
            Transactions for {useVerification ? `${customFromDate.toLocaleDateString()} - ${customToDate.toLocaleDateString()}` : periodName}
          </h3>
          <button
            onClick={useVerification ? fetchVerifiedTransactions : handleRefresh}
            disabled={loadingVerification || loadingTransactions}
            className="flex items-center space-x-2 px-4 py-2 bg-[#61CE70]/20 rounded-xl hover:bg-[#61CE70]/30 disabled:bg-gray-600/20 disabled:cursor-not-allowed transition-colors text-[#61CE70]"
          >
            <RefreshCw className="w-4 h-4" />
            <span>{loadingVerification || loadingTransactions ? "Loading..." : "Refresh"}</span>
          </button>
        </div>

        {(loadingTransactions || loadingVerification) ? (
          <div className="text-center text-gray-400">
            Loading transactions...
          </div>
        ) : (useVerification ? verifiedTransactions.length === 0 : displayTransactions.length === 0) ? (
          <div className="text-center text-gray-400">
            No transactions found for {useVerification ? `${customFromDate.toLocaleDateString()} - ${customToDate.toLocaleDateString()}` : periodName}.
          </div>
        ) : (
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
            {(useVerification ? verifiedTransactions : displayTransactions).map((tx) => (
              <div key={tx.id} className="space-y-0">
                <div
                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 sm:p-6 bg-gray-800/30 rounded-2xl hover:bg-gray-800/50 transition-colors cursor-pointer"
                  onClick={() => useVerification && toggleExpandDetails(tx.id)}
                >
                  <div className="flex items-start sm:items-center space-x-4 w-full sm:w-auto">
                    <div
                      className={`p-3 rounded-xl shrink-0 ${
                        tx.type === "received"
                          ? "bg-[#61CE70]/20"
                          : "bg-red-500/20"
                      }`}
                    >
                      {tx.type === "received" ? (
                        <ArrowDownLeft className="w-5 h-5 text-[#61CE70]" />
                      ) : (
                        <ArrowUpRight className="w-5 h-5 text-red-400" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium capitalize mb-1">
                        {tx.type === "received" ? "Deposit" : "Transfer"}
                      </p>
                      <p className="text-gray-400 text-sm break-words">
                        {tx.type === "received" ? (
                          <span>
                            From:{" "}
                            <span className="font-bold text-white uppercase">
                              {getCounterpartyDisplay(tx)}
                            </span>
                          </span>
                        ) : (
                          <span>
                            To:{" "}
                            <span className="font-bold text-white uppercase">
                              {getCounterpartyDisplay(tx)}
                            </span>
                          </span>
                        )}
                      </p>
                      <p className="text-gray-500 text-xs">{tx.date}</p>
                    </div>
                  </div>
                  <div className="mt-4 sm:mt-0 text-left sm:text-right space-y-2 w-full sm:w-auto">
                    <p
                      className={`text-base sm:text-lg font-bold ${
                        tx.type === "received" ? "text-[#61CE70]" : "text-red-400"
                      }`}
                    >
                      {tx.type === "received" ? "+" : "-"}
                      {formatAmount(tx.amount)} USD
                    </p>
                    <div className="flex flex-wrap sm:justify-end gap-2">
                      <button
                        onClick={() => copyToClipboard(tx.hash)}
                        className="text-gray-400 hover:text-white text-xs flex items-center space-x-1 transition-colors"
                      >
                        <span className="hidden sm:inline">
                          {truncateAddress(tx.hash)}
                        </span>
                        <span className="sm:hidden">Hash</span>
                        <Copy className="w-3 h-3" />
                      </button>
                      <button
                        onClick={() => viewOnBlockExplorer(tx.hash)}
                        className="text-gray-400 hover:text-white text-xs flex items-center space-x-1 transition-colors"
                      >
                        <span>Explorer</span>
                        <ExternalLink className="w-3 h-3" />
                      </button>
                      {useVerification && (
                        <button className="text-gray-400 hover:text-white text-xs flex items-center space-x-1 transition-colors">
                          {expandedDetails[tx.id] ? (
                            <>
                              <span>Details</span>
                              <ChevronUp className="w-3 h-3" />
                            </>
                          ) : (
                            <>
                              <span>Details</span>
                              <ChevronDown className="w-3 h-3" />
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Verification Details (expanded) */}
                {useVerification && expandedDetails[tx.id] && (
                  <div className="bg-gray-700/20 rounded-xl p-4 ml-16 mr-4 border-l-2 border-blue-500">
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-400">Found in Database:</span>
                        <span className="text-white font-medium">
                          {(tx as VerifiedTransaction).verificationDetails.foundInDb ? "✓ Yes" : "✗ No"}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Found on-chain:</span>
                        <span className="text-white font-medium">
                          {(tx as VerifiedTransaction).verificationDetails.foundOnChain ? "✓ Yes" : "✗ No"}
                        </span>
                      </div>
                      {(tx as VerifiedTransaction).verificationDetails.hashMatch !== undefined && (
                        <div className="flex justify-between">
                          <span className="text-gray-400">Hash Match:</span>
                          <span className="text-white font-medium">
                            {(tx as VerifiedTransaction).verificationDetails.hashMatch ? "✓ Yes" : "✗ No"}
                          </span>
                        </div>
                      )}
                      {(tx as VerifiedTransaction).verificationDetails.amountMatch !== undefined && (
                        <div className="flex justify-between">
                          <span className="text-gray-400">Amount Match:</span>
                          <span className="text-white font-medium">
                            {(tx as VerifiedTransaction).verificationDetails.amountMatch ? "✓ Yes" : "✗ No"}
                          </span>
                        </div>
                      )}
                      {(tx as VerifiedTransaction).verificationDetails.discrepancies &&
                        (tx as VerifiedTransaction).verificationDetails.discrepancies.length > 0 && (
                          <div>
                            <span className="text-gray-400">Issues:</span>
                            <ul className="text-yellow-400 mt-1 space-y-1">
                              {(tx as VerifiedTransaction).verificationDetails.discrepancies.map((disc, idx) => (
                                <li key={idx} className="text-xs">
                                  • {disc}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalyticsPage;
